import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { CodeBlock } from "@/ui/CodeBlock";

/* Markdown заметок базы знаний «как есть» (спека SP-KB §2): заголовки, абзацы,
   списки, таблицы, цитаты и коллауты Obsidian, код с подсветкой, формулы $…$
   моноширинным без рендера (KaTeX — позже). Ссылки /notes/… — внутри SPA. */

const INLINE = /(`[^`]+`|\$\$[^$]+\$\$|\$[^$\n]+\$|!\[[^\]]*\]\([^)]*\)|\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*\n]+\*)/g;

export function inline(text: string): ReactNode[] {
  return text
    .split(INLINE)
    .filter(Boolean)
    .map((p, i) => {
      if (p.startsWith("`"))
        return (
          <code key={i} className="rounded-[3px] bg-bg2 px-1 py-px font-mono text-[0.92em] text-ink">
            {p.slice(1, -1)}
          </code>
        );
      if (p.startsWith("$"))
        return (
          <code key={i} className="font-mono text-[0.92em] text-ink2" title="формула">
            {p.replace(/^\$+|\$+$/g, "")}
          </code>
        );
      if (p.startsWith("!["))
        return (
          <span key={i} className="font-mono text-[12px] text-mut">
            [изображение{/!\[([^\]]*)\]/.exec(p)?.[1] ? `: ${/!\[([^\]]*)\]/.exec(p)![1]}` : ""}]
          </span>
        );
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(p);
      if (link) {
        const [, label, href] = link as unknown as [string, string, string];
        if (href.startsWith("/"))
          return (
            <Link key={i} to={href} className="text-acc underline decoration-bd2 underline-offset-2 hover:decoration-acc">
              {label}
            </Link>
          );
        return (
          <a key={i} href={href} target="_blank" rel="noreferrer" className="text-acc underline decoration-bd2 underline-offset-2 hover:decoration-acc">
            {label}
          </a>
        );
      }
      if (p.startsWith("**"))
        return (
          <strong key={i} className="text-ink">
            {p.slice(2, -2)}
          </strong>
        );
      if (p.startsWith("*")) return <em key={i}>{p.slice(1, -1)}</em>;
      return p;
    });
}

type Block =
  | { t: "h"; level: number; text: string }
  | { t: "p"; text: string }
  | { t: "ul" | "ol"; items: string[] }
  | { t: "code"; lang: string; code: string }
  | { t: "quote"; kind: string; title: string; lines: string[] }
  | { t: "table"; rows: string[][] }
  | { t: "hr" };

export function parse(md: string): Block[] {
  const out: Block[] = [];
  const lines = md.replace(/\r/g, "").split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === "") {
      i++;
      continue;
    }
    const fence = /^```(\w*)\s*$/.exec(line);
    if (fence) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i]!)) code.push(lines[i++]!);
      i++;
      out.push({ t: "code", lang: fence[1] || "python", code: code.join("\n") });
      continue;
    }
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      out.push({ t: "h", level: h[1]!.length, text: h[2]! });
      i++;
      continue;
    }
    if (/^---+\s*$/.test(line)) {
      out.push({ t: "hr" });
      i++;
      continue;
    }
    if (line.startsWith(">")) {
      const q: string[] = [];
      while (i < lines.length && lines[i]!.startsWith(">")) q.push(lines[i++]!.replace(/^>\s?/, ""));
      const callout = /^\[!(\w+)\][-+]?\s*(.*)$/.exec(q[0] ?? "");
      out.push(callout ? { t: "quote", kind: callout[1]!.toLowerCase(), title: callout[2]!, lines: q.slice(1) } : { t: "quote", kind: "", title: "", lines: q });
      continue;
    }
    if (line.startsWith("|")) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i]!.startsWith("|")) {
        const cells = lines[i]!.replace(/^\||\|\s*$/g, "").split("|").map((c) => c.trim());
        if (!cells.every((c) => /^:?-+:?$/.test(c))) rows.push(cells);
        i++;
      }
      out.push({ t: "table", rows });
      continue;
    }
    const li = /^\s*([-*]|\d+\.)\s+/.exec(line);
    if (li) {
      const items: string[] = [];
      const ordered = /\d/.test(li[1]!);
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*([-*]|\d+\.)\s+/, ""));
      out.push({ t: ordered ? "ol" : "ul", items });
      continue;
    }
    const p: string[] = [];
    while (i < lines.length && lines[i]!.trim() !== "" && !/^(#{1,6}\s|```|>|\||\s*([-*]|\d+\.)\s|---+\s*$)/.test(lines[i]!)) p.push(lines[i++]!);
    out.push({ t: "p", text: p.join(" ") });
  }
  return out;
}

const H_CLASS: Record<number, string> = {
  1: "text-[22px]",
  2: "text-[18px]",
  3: "text-[15.5px]",
  4: "text-[14px]",
};

export function Markdown({ text }: { text: string }) {
  const blocks = parse(text);
  return (
    <div className="flex flex-col gap-3.5 text-[14px] leading-[1.65] text-ink2">
      {blocks.map((b, i) => {
        switch (b.t) {
          case "h": {
            const Tag = `h${Math.min(b.level + 1, 6)}` as "h2" | "h3" | "h4" | "h5" | "h6";
            return (
              <Tag key={i} className={`m-0 mt-2 font-mono font-semibold text-ink ${H_CLASS[b.level] ?? "text-[13px]"}`}>
                {inline(b.text)}
              </Tag>
            );
          }
          case "p":
            return (
              <p key={i} className="m-0 text-pretty">
                {inline(b.text)}
              </p>
            );
          case "ul":
          case "ol": {
            const L = b.t;
            return (
              <L key={i} className={`m-0 flex flex-col gap-1 pl-5 ${L === "ul" ? "list-disc" : "list-decimal"}`}>
                {b.items.map((it, j) => (
                  <li key={j}>{inline(it)}</li>
                ))}
              </L>
            );
          }
          case "code":
            return <CodeBlock key={i} code={b.code} label={b.lang} />;
          case "quote":
            return (
              <blockquote key={i} className="m-0 rounded-[6px] border border-bd border-l-2 border-l-accdim bg-card px-3.5 py-2.5">
                {b.title && <div className="mb-1 font-mono text-[11.5px] uppercase tracking-wide text-accdim">{b.title}</div>}
                {!b.title && b.kind && <div className="mb-1 font-mono text-[11.5px] uppercase tracking-wide text-accdim">{b.kind}</div>}
                <Markdown text={b.lines.join("\n")} />
              </blockquote>
            );
          case "table":
            return (
              <div key={i} className="overflow-x-auto">
                <table className="w-full border-collapse font-sans text-[13px]">
                  <tbody>
                    {b.rows.map((r, ri) => (
                      <tr key={ri} className="border-b border-bd">
                        {r.map((c, ci) =>
                          ri === 0 ? (
                            <th key={ci} className="px-2.5 py-1.5 text-left font-mono text-[11.5px] font-semibold text-mut">
                              {inline(c)}
                            </th>
                          ) : (
                            <td key={ci} className="px-2.5 py-1.5 align-top">
                              {inline(c)}
                            </td>
                          ),
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case "hr":
            return <hr key={i} className="m-0 border-0 border-t border-bd" />;
        }
      })}
    </div>
  );
}
